/**
 * LYRICS — the workspace's right column, and the lyric editor.
 *
 * In the drawer this was collapsed behind a disclosure, because the drawer was a
 * 420px column and an open lyric sheet pushed everything else off the bottom. A
 * workspace has room for it, and lyrics are something you read *while* you listen,
 * so here it is open and permanent.
 *
 * TWO MODES
 * ---------
 *   reading — headings picked out, body lines set for reading. Copy, download and
 *             print act on the sheet, and Edit switches modes.
 *   editing — one textarea holding the whole sheet, with Save and Cancel.
 *
 * A single textarea rather than a per-section editor. The sheet's structure is its
 * own text — a line reading `[Chorus]` IS the chorus heading, which is what
 * lib/sections.ts reads for the timeline's markers — so editing the text is how you
 * add, remove, rename or reorder a section. A structured editor would have to parse
 * the sheet into sections, render controls for each, and serialise back; it would
 * fight the format instead of exposing it, and it would make "delete this section"
 * possible while "move this line up" was not.
 *
 * THE DRAFT
 * ---------
 * `draft` is local, and it is the only copy of the unsaved text. It is deliberately
 * not in studioStore: a second home for the lyrics raises the question of which copy
 * is current, and the answer is eventually wrong. What leaves this component is the
 * text itself, handed to onSave or onRegenerate at the moment the button is pressed.
 *
 * Reset across songs is handled by the caller keying this component on the song id —
 * see StudioWorkspace. That is why there is no effect syncing `draft` to the prop: an
 * effect that overwrote the draft whenever the song record changed would also discard
 * an in-progress edit every time anything else about the song was patched.
 *
 * REGENERATE
 * ----------
 * Regenerate Song sends the text that is on screen — including edits not yet saved,
 * which is the whole point of it living here. It goes through the Studio's existing
 * confirm-then-charge flow (useStudio.runRegenerate → the credit dialog →
 * generationApi.runStudioAction), so it shows the same confirmation and costs credits
 * like every other Studio action.
 *
 * SIMULATED. Regeneration produces no new audio — there is no audio. It records a
 * version and writes the lyrics; see useStudio.ts for exactly what a real backend
 * would change.
 *
 * The column is a labelled region, as the other two are: three permanent panels
 * side by side is exactly the case landmarks exist for, and it also means the same
 * section name appearing here and on the timeline is unambiguous.
 *
 * READING MODE IS songs/LyricSheet NOW — Batch 6 §10. This file used to render the sheet
 * itself, against its own copy of the heading regex, and so did modals/LyricsModal; §10's
 * "lyrics follow the song" belongs to the sheet rather than to one of its two surfaces, so
 * the two renderers became one component. `at` is what this panel contributes: the song's
 * share of its length, which the workspace already has in hand for the timeline's
 * playhead. What the highlight claims — a SECTION, approximately, never a line and never a
 * word — is documented there, and this panel's editing mode is untouched by it.
 */
import { useState } from 'react';
import { Copy, Download, Pencil, Printer, RefreshCw } from 'lucide-react';
import { Btn, IconButton, Tooltip } from '../ui';
import { LyricSheet } from '../songs';
import { c } from '../../styles/theme';
import type { Song, StudioActionKind } from '../../types/domain';

export interface LyricsPanelProps {
  className?: string;
  song: Song;
  onCopy: () => void;
  onDownload: () => void;
  onPrint: () => void;
  /** Persists edited lyrics without regenerating. Free — it is a text edit. */
  onSave: (lyrics: string) => void;
  /** Queues a regeneration from the given lyrics, behind the credit confirmation. */
  onRegenerate: (lyrics: string) => void;
  /** True while a Studio action is running — the button must not queue a second. */
  processing: boolean;
  processingActionKind?: StudioActionKind | null;
  /**
   * Playback position as a share of this song's length, 0–1 — used by the untimed fallback
   * when this song has no real lyric timings.
   */
  at?: number | null;
  /**
   * Playback position in seconds — used when the song carries real line timings.
   *
   * Only reading mode uses it. An edit is not a listen: the textarea is one field holding
   * the whole sheet, and there is no honest way to highlight a section or line inside it.
   */
  elapsedSec?: number | null;
}

export default function LyricsPanel({
  className,
  song,
  onCopy,
  onDownload,
  onPrint,
  onSave,
  onRegenerate,
  processing,
  processingActionKind,
  at = null,
  elapsedSec = null,
}: LyricsPanelProps) {
  const [draft, setDraft] = useState(song.lyrics);
  const [editing, setEditing] = useState(false);

  // Compared against the saved sheet rather than tracked with a flag, so undoing an
  // edit by hand correctly leaves nothing to save.
  const dirty = draft !== song.lyrics;
  const hasText = Boolean(draft.trim());

  function save() {
    onSave(draft);
    setEditing(false);
  }

  function cancel() {
    // Back to the saved sheet. The draft is discarded rather than kept for later:
    // a "cancelled" edit that silently reappears next time is worse than losing it.
    setDraft(song.lyrics);
    setEditing(false);
  }

  return (
    <section className={['lyx-ws-lyrics', className].filter(Boolean).join(' ')} aria-label="Lyrics">
      <div className="lyx-ws-lyrics-head">
        <p className="lyx-ws-eyebrow lyx-ws-lyrics-label" style={{ margin: 0 }}>
          Lyrics
        </p>

        <div className="lyx-ws-lyrics-actions lyx-row-controls">
          {/* Edit is offered even with no lyrics at all — that is how a sheet gets
              written for a song that has none. The export actions are not, because
              there would be nothing to export. */}
          {!editing && (
            <IconButton label="Edit lyrics" onClick={() => setEditing(true)}>
              <Pencil size={13} color={c.text2} />
            </IconButton>
          )}
          {!editing && song.lyrics && (
            <>
              <IconButton label="Copy lyrics" onClick={onCopy}>
                <Copy size={13} color={c.text2} />
              </IconButton>
              <IconButton label="Download lyrics" onClick={onDownload}>
                <Download size={13} color={c.text2} />
              </IconButton>
              <IconButton label="Print lyrics" onClick={onPrint}>
                <Printer size={13} color={c.text2} />
              </IconButton>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="lyx-ws-lyrics-body">
          {/* Labelled visibly-hiddenly: the eyebrow above says "Lyrics" but it is
              a heading, not this field's label. */}
          <label htmlFor="lyx-lyrics-editor" className="lyx-visually-hidden">
            Lyrics for {song.title}
          </label>
          <textarea
            id="lyx-lyrics-editor"
            className="lyx-ws-lyrics-editor"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            placeholder={'Verse 1\nWrite the first line here…'}
            style={{
              background: c.surface2,
              border: `1px solid ${c.borderStrong}`,
              color: c.text1,
            }}
          />
          <p style={{ fontSize: 10.5, color: c.text2, margin: '7px 0 0', lineHeight: 1.6 }}>
            Put a section name on its own line — <code>Chorus</code> — and it becomes a heading here
            and a marker on the timeline.
          </p>
        </div>
      ) : song.lyrics ? (
        /*
          The scroll box is handed to LyricSheet rather than wrapped around it, and that is
          the reason `className` exists on that component: it renders the follow-note as a
          SIBLING of the blocks, so passing `.lyx-ws-lyrics-body` down leaves the note
          pinned under the scrolling sheet instead of scrolling away inside it.
        */
        <LyricSheet
          lyrics={song.lyrics}
          at={at}
          elapsedSec={elapsedSec}
          lyricTimings={song.lyricTimings}
          lyricSectionTimings={song.lyricSectionTimings}
          variant="panel"
          className="lyx-ws-lyrics-body"
        />
      ) : (
        <div className="lyx-ws-lyrics-body">
          <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
            No lyrics for this track yet. Use Edit to write them.
          </p>
        </div>
      )}

      {/* ---- the footer: save the text, or re-sing it ----
          Pinned below the scrolling sheet rather than after it, so Regenerate is
          reachable without scrolling a long lyric sheet to its end. */}
      <div className="lyx-ws-lyrics-foot" style={{ borderTop: `1px solid ${c.border}` }}>
        {editing && (
          <div className="lyx-ws-lyrics-edit-actions">
            <Btn onClick={save} disabled={!dirty} style={{ flex: '1 1 0' }}>
              Save
            </Btn>
            <Btn variant="secondary" onClick={cancel} style={{ flex: '1 1 0' }}>
              Cancel
            </Btn>
          </div>
        )}

        {/* Regenerate takes the draft, not the song, so an unsaved edit is what gets
            re-sung. Disabled on an empty sheet: there is nothing to regenerate
            from, and studioStore.buildAction would refuse the action anyway.

            `describe`, and a span around the button: the bubble says which text is
            sent, which the label "Regenerate song" does not, and a disabled button
            emits no pointer events — so without the wrapper the bubble would be
            missing on the empty sheet, the one state where "what would this even
            use?" is the question being asked. */}
        <Tooltip label="Regenerate using the edited lyrics" describe style={{ width: '100%' }}>
          <span style={{ display: 'block', width: '100%' }}>
            <Btn
              variant="secondary"
              full
              onClick={() => onRegenerate(draft)}
              disabled={processing || !hasText}
            >
              <RefreshCw size={13} />
              {processing && processingActionKind === 'regenerate'
                ? 'Regenerating song…'
                : 'Regenerate song'}
            </Btn>
          </span>
        </Tooltip>

        {/*
          Shown WHILE editing, which is the only time an unsaved edit exists — both
          Save and Cancel leave the draft equal to the stored sheet. This was
          `dirty && !editing`, a condition nothing could satisfy, so the warning never
          appeared at the one moment it is needed: Regenerate sits directly below it
          and is about to spend credits on text the user has not committed.
        */}
        {dirty && (
          <p style={{ fontSize: 10.5, color: c.accent, margin: 0, lineHeight: 1.5 }}>
            Unsaved lyric changes — Regenerate will use them.
          </p>
        )}
      </div>
    </section>
  );
}
